import React, { useState, useEffect, useCallback, useRef } from 'react';
import { cldThumb } from '../utils/img';
import { useSizes, useCategories } from '../api/meta';
import {
  getBenchmarkBrands, getBenchmarkResults, exportBenchmarkResults, startBenchmarkRun,
  getBenchmarkRuns, getBenchmarkLinks, downloadLinksTemplate, uploadBenchmarkLinks,
} from '../api/benchmark';

// Brand Ops -> Benchmarking. Live market prices per design link (backend:
// routers/benchmark.py). Three views: Results (default), Links, Health.

const PAGE_SIZE = 50;
const SITES = [
  { key: 'own_site', label: 'Brand site' },
  { key: 'myntra', label: 'Myntra' },
  { key: 'firstcry', label: 'FirstCry' },
  { key: 'amazon', label: 'Amazon' },
  { key: 'flipkart', label: 'Flipkart' },
];
const SITE_LABEL = Object.fromEntries(SITES.map(s => [s.key, s.label]));
const GROUP_STYLE = {
  cheapest: 'bg-green-100 text-green-700',
  higher_10: 'bg-yellow-100 text-yellow-800',
  higher_25: 'bg-orange-100 text-orange-800',
  higher_25_plus: 'bg-pink-100 text-zoddle-pink',
  no_market_price: 'bg-gray-100 text-gray-500',
  verify_link: 'bg-gray-200 text-gray-700',
  not_checked: 'bg-gray-50 text-gray-400',
};
const STATUS_TEXT = {
  out_of_stock: 'OOS', no_price: 'no price', not_fetched: 'not fetched',
  not_allowed: 'not allowed', not_in_rupees: 'not ₹', check_page: 'check page',
};

const selectCls = 'border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700 bg-white focus:outline-none focus:border-zoddle-green';
const btnCls = 'text-xs border border-gray-200 rounded-lg px-3 py-1.5 text-gray-600 hover:border-zoddle-green font-medium disabled:opacity-40';
const rupees = n => (n === null || n === undefined) ? '—' : '₹' + Math.round(n).toLocaleString('en-IN');
const errText = e => e?.response?.data?.detail || e?.message || 'Something went wrong.';

function istTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}
function scopeText(r) {
  if (!r) return '';
  if (r.scope_type === 'all') return 'Whole inventory';
  if (r.scope_type === 'brand') return r.scope_value;
  const n = (r.scope_value || '').split(',').filter(Boolean).length;
  return `${n} barcode${n === 1 ? '' : 's'}`;
}

// ------------------------------------------------------------------ run bar

function RunBar({ brands, onFinished }) {
  const [scope, setScope] = useState('brand');
  const [brand, setBrand] = useState('');
  const [barcodes, setBarcodes] = useState('');
  const [run, setRun] = useState(null);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(false);
  const wasRunning = useRef(false);

  const poll = useCallback(async () => {
    try {
      const runs = await getBenchmarkRuns(1);
      const latest = runs[0] || null;
      setRun(latest);
      const running = latest?.status === 'running';
      if (wasRunning.current && !running) onFinished();
      wasRunning.current = running;
    } catch { /* keep the last known state */ }
  }, [onFinished]);

  useEffect(() => { poll(); }, [poll]);
  useEffect(() => {
    if (run?.status !== 'running') return undefined;
    const t = setInterval(poll, 4000);
    return () => clearInterval(t);
  }, [run?.status, poll]);

  async function start() {
    setError(null);
    const body = { scope };
    if (scope === 'brand') {
      if (!brand) { setError('Pick a brand.'); return; }
      body.brand = brand;
    }
    if (scope === 'barcodes') {
      if (!barcodes.trim()) { setError('Paste at least one Zoddle barcode.'); return; }
      body.barcodes = barcodes;
    }
    if (scope === 'all' && !window.confirm('Check every linked piece in the inventory? This can take a long time.')) return;
    setStarting(true);
    try {
      const r = await startBenchmarkRun(body);
      setRun(r); wasRunning.current = true;
    } catch (e) { setError(errText(e)); }
    finally { setStarting(false); }
  }

  const running = run?.status === 'running';
  const pct = run && run.skus_total ? Math.round((run.skus_done / run.skus_total) * 100) : 0;
  const linkedBrands = brands.filter(b => b.designs_linked > 0);

  return (
    <div className="bg-white border border-gray-100 rounded-xl p-4 mb-4">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-semibold text-gray-700 mr-1">Run a price check</span>
        <select value={scope} onChange={e => setScope(e.target.value)} className={selectCls} disabled={running}>
          <option value="brand">One brand</option>
          <option value="barcodes">Specific barcodes</option>
          <option value="all">Whole inventory</option>
        </select>
        {scope === 'brand' && linkedBrands.length === 0 && (
          <span className="text-xs text-gray-500">No brands have links yet — add them in the <b>Links</b> tab.</span>
        )}
        {scope === 'brand' && linkedBrands.length > 0 && (
          <select value={brand} onChange={e => setBrand(e.target.value)} className={selectCls} disabled={running}>
            <option value="">Select brand…</option>
            {linkedBrands.map(b => (
              <option key={b.brand} value={b.brand}>{b.brand} ({b.designs_linked} linked)</option>
            ))}
          </select>
        )}
        {scope === 'barcodes' && (
          <input value={barcodes} onChange={e => setBarcodes(e.target.value)} disabled={running}
            placeholder="Zoddle barcodes, comma or space separated"
            className={selectCls + ' w-96'} />
        )}
        <button onClick={start} disabled={running || starting}
          className="bg-zoddle-green text-white rounded-lg px-4 py-1.5 text-sm font-semibold disabled:opacity-40">
          {starting ? 'Starting…' : 'Run'}
        </button>
        <div className="flex-1" />
        {run && !running && (
          <span className="text-xs text-gray-400">
            Last run: {scopeText(run)} · {run.status} · {istTime(run.finished_at || run.started_at)}
            {run.started_by ? ` · ${run.started_by}` : ''}
          </span>
        )}
      </div>
      {running && (
        <div className="mt-3">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>Checking {scopeText(run)} — {run.skus_done} of {run.skus_total || '…'} SKUs · {run.pages_fetched} pages read</span>
            <span>{pct}%</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-zoddle-green transition-all" style={{ width: `${pct}%` }} />
          </div>
          <p className="text-[11px] text-gray-400 mt-1">Sites are read politely (about one page a second, Amazon every 3s). You can leave this page; the run carries on.</p>
        </div>
      )}
      {run && !running && run.error && (
        <p className="text-xs text-gray-500 mt-2">Note: {run.error}</p>
      )}
      {error && <p className="text-xs text-zoddle-pink mt-2">{error}</p>}
    </div>
  );
}

// ------------------------------------------------------------------ results

function SiteCell({ cell }) {
  if (!cell) return <span className="text-gray-200">—</span>;
  const href = cell.page_url || cell.link;
  const priced = cell.status === 'priced' && cell.price !== null;
  let text, cls;
  if (priced) {
    text = rupees(cell.price);
    cls = cell.confirmed ? 'text-zoddle-blue font-semibold' : 'text-gray-500 font-semibold';
  } else {
    text = STATUS_TEXT[cell.status] || cell.status;
    if (cell.status === 'out_of_stock' && cell.price !== null) text += ` (${rupees(cell.price)})`;
    cls = cell.status === 'not_fetched' ? 'text-orange-500' : 'text-gray-400';
  }
  const tip = [cell.note, cell.stale ? `Last good read ${istTime(cell.fetched_at)}; latest read failed ${istTime(cell.last_attempt_at)}` : `Read ${istTime(cell.fetched_at || cell.last_attempt_at)}`]
    .filter(Boolean).join('\n');
  return (
    <a href={href} target="_blank" rel="noreferrer" title={tip} className={`${cls} hover:underline whitespace-nowrap`}>
      {text}{!priced || cell.confirmed ? '' : ' ?'}{cell.stale ? ' ⚠' : ''}
    </a>
  );
}

function ResultsView({ brands }) {
  const sizes = useSizes();
  const categories = useCategories();
  const [filters, setFilters] = useState({ brand: '', category: '', size: '', group: '', search: '', include_sold: false });
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let live = true;
    setLoading(true); setError(null);
    getBenchmarkResults({ ...filters, page, page_size: PAGE_SIZE })
      .then(d => { if (live) setData(d); })
      .catch(e => { if (live) setError(errText(e)); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [filters, page, tick]);

  const setF = (k, v) => { setFilters(f => ({ ...f, [k]: v })); setPage(1); };
  const onRunFinished = useCallback(() => setTick(t => t + 1), []);

  async function doExport() {
    setExporting(true);
    try { await exportBenchmarkResults(filters); }
    catch (e) { setError(errText(e)); }
    finally { setExporting(false); }
  }

  const pages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const linkedBrands = brands.filter(b => b.designs_linked > 0);

  return (
    <>
      <RunBar brands={brands} onFinished={onRunFinished} />

      <div className="flex items-center gap-2 flex-wrap mb-3">
        <select value={filters.brand} onChange={e => setF('brand', e.target.value)} className={selectCls}>
          <option value="">All brands</option>
          {linkedBrands.map(b => <option key={b.brand} value={b.brand}>{b.brand}</option>)}
        </select>
        <select value={filters.category} onChange={e => setF('category', e.target.value)} className={selectCls}>
          <option value="">All categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filters.size} onChange={e => setF('size', e.target.value)} className={selectCls}>
          <option value="">All sizes</option>
          {sizes.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <form onSubmit={e => { e.preventDefault(); setF('search', searchText.trim()); }}>
          <input value={searchText} onChange={e => setSearchText(e.target.value)}
            placeholder="Barcode, design ID or name" className={selectCls + ' w-56'} />
        </form>
        <label className="text-xs text-gray-500 flex items-center gap-1 ml-1">
          <input type="checkbox" checked={filters.include_sold} onChange={e => setF('include_sold', e.target.checked)} />
          Include sold / gone
        </label>
        <div className="flex-1" />
        <button onClick={doExport} disabled={exporting || !data?.total} className={btnCls}>
          {exporting ? 'Preparing…' : 'Download Excel'}
        </button>
      </div>

      {data && (
        <div className="flex gap-2 flex-wrap mb-3">
          <button onClick={() => setF('group', '')}
            className={`text-xs px-3 py-1 rounded-full border ${!filters.group ? 'border-zoddle-green text-zoddle-green font-semibold' : 'border-gray-200 text-gray-500'}`}>
            All ({data.groups.reduce((s, g) => s + g.count, 0)})
          </button>
          {data.groups.filter(g => g.count > 0).map(g => (
            <button key={g.key} onClick={() => setF('group', filters.group === g.key ? '' : g.key)}
              className={`text-xs px-3 py-1 rounded-full ${GROUP_STYLE[g.key]} ${filters.group === g.key ? 'ring-2 ring-zoddle-green font-semibold' : ''}`}>
              {g.label} ({g.count})
            </button>
          ))}
        </div>
      )}

      {error && <div className="mb-3 bg-red-50 border border-red-100 rounded-lg px-4 py-3 text-sm text-zoddle-pink">{error}</div>}
      {loading && !data && <p className="text-sm text-gray-400">Loading…</p>}
      {data && data.total === 0 && (
        <p className="text-sm text-gray-400">
          No pieces here yet. Add links in the <b>Links</b> view, then run a price check.
        </p>
      )}

      {data && data.total > 0 && (
        <div className="bg-white border border-gray-100 rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-400 border-b">
                <th className="px-3 py-2">Piece</th>
                <th className="px-2 py-2 text-right">Our price</th>
                <th className="px-2 py-2 text-right">Cost</th>
                {SITES.map(s => <th key={s.key} className="px-2 py-2 text-right">{s.label}</th>)}
                <th className="px-2 py-2 text-right">Cheapest</th>
                <th className="px-2 py-2 text-right">Gap</th>
                <th className="px-3 py-2">Result</th>
              </tr>
            </thead>
            <tbody className={loading ? 'opacity-50' : ''}>
              {data.pieces.map(p => (
                <tr key={p.zoddle_barcode} className="border-b last:border-0 align-top">
                  <td className="px-3 py-2">
                    <div className="flex gap-2 items-start min-w-[220px]">
                      {p.image_url_1
                        ? <img src={cldThumb(p.image_url_1, 96)} alt="" loading="lazy" className="w-12 h-12 object-cover rounded bg-gray-100 flex-shrink-0" />
                        : <div className="w-12 h-12 rounded bg-gray-100 flex-shrink-0" />}
                      <div className="min-w-0">
                        <p className="font-mono font-bold text-zoddle-blue text-xs">{p.zoddle_barcode}</p>
                        <p className="text-xs text-gray-700 truncate max-w-[220px]" title={p.design_name}>{p.design_name}</p>
                        <p className="text-[11px] text-gray-400">{p.brand} · {p.design_code} · {p.size}
                          {p.status !== 'in_bin' ? ` · ${p.status.replace(/_/g, ' ')}` : ''}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-2 text-right font-semibold whitespace-nowrap">{rupees(p.our_price)}</td>
                  <td className="px-2 py-2 text-right text-gray-500 whitespace-nowrap">{rupees(p.cost)}</td>
                  {SITES.map(s => <td key={s.key} className="px-2 py-2 text-right"><SiteCell cell={p.sites[s.key]} /></td>)}
                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    <span className="font-semibold">{rupees(p.cheapest_price)}</span>
                    {p.cheapest_sites.length > 0 && (
                      <p className="text-[11px] text-gray-400">{p.cheapest_sites.map(s => SITE_LABEL[s] || s).join(' / ')}</p>
                    )}
                  </td>
                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    {p.gap === null ? '—' : (
                      <span className={p.gap > 1 ? 'text-zoddle-pink' : 'text-green-700'}
                        title={p.gap > 0 ? 'We are dearer than the cheapest market price by this much' : 'We are cheaper than (or equal to) the cheapest market price'}>
                        {p.gap > 0 ? '+' : p.gap < 0 ? '−' : ''}{rupees(Math.abs(p.gap))}
                        <span className="text-[11px] text-gray-400 ml-1">{p.gap_pct > 0 ? '+' : p.gap_pct < 0 ? '−' : ''}{Math.abs(p.gap_pct)}%</span>
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full whitespace-nowrap ${GROUP_STYLE[p.group]}`}>{p.group_label}</span>
                    {p.below_cost && <p className="text-[11px] text-zoddle-pink font-semibold mt-1" title="The cheapest market price is below our cost incl GST">below our cost</p>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && data.total > PAGE_SIZE && (
        <div className="flex items-center justify-center gap-3 mt-4 text-xs text-gray-500">
          <button onClick={() => setPage(p => p - 1)} disabled={page <= 1} className={btnCls}>← Previous</button>
          <span>Page {page} of {pages} · {data.total.toLocaleString()} pieces</span>
          <button onClick={() => setPage(p => p + 1)} disabled={page >= pages} className={btnCls}>Next →</button>
        </div>
      )}

      <p className="text-[11px] text-gray-400 mt-4">
        Our price = Zoddle price, or sale price if lower. Cheapest counts only in-stock prices the engine could tie to the design.
        <b> ?</b> = price found but not confirmed for this design · <b>⚠</b> = latest read failed, showing the last good price (hover for times).
        Prices change daily; bank-card offers are ignored.
      </p>
    </>
  );
}

// ------------------------------------------------------------------ links

function UploadPanel({ onApplied }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);
  const inputRef = useRef(null);

  async function doPreview(f) {
    setFile(f); setPreview(null); setDone(null); setError(null);
    if (!f) return;
    setBusy(true);
    try { setPreview(await uploadBenchmarkLinks(f, 'preview')); }
    catch (e) { setError(errText(e)); }
    finally { setBusy(false); }
  }
  async function doCommit() {
    setBusy(true); setError(null);
    try {
      const r = await uploadBenchmarkLinks(file, 'commit');
      setDone(r); setPreview(null); setFile(null);
      if (inputRef.current) inputRef.current.value = '';
      onApplied();
    } catch (e) { setError(errText(e)); }
    finally { setBusy(false); }
  }
  const s = preview?.summary || {};

  return (
    <div className="bg-white border border-gray-100 rounded-xl p-4 mb-4">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-semibold text-gray-700 mr-1">Add or change links</span>
        <button onClick={() => downloadLinksTemplate().catch(e => setError(errText(e)))} className={btnCls}>Download template</button>
        <input ref={inputRef} type="file" accept=".csv,.xlsx" onChange={e => doPreview(e.target.files[0] || null)}
          className="text-xs text-gray-600" />
        {busy && <span className="text-xs text-gray-400">Working…</span>}
      </div>
      <p className="text-[11px] text-gray-400 mt-2">
        Columns: <b>Design ID, Link 1 … Link 5</b>. One link per site per design, in any column. A blank cell leaves that link alone;
        write <b>delete</b> to remove the link in that column. Short links (amzn.in) and sites that refuse us (Ajio, Meesho, H&amp;M) are rejected.
      </p>

      {error && <p className="text-xs text-zoddle-pink mt-2">{error}</p>}
      {done && (
        <p className="text-xs text-green-700 mt-2">
          Saved: {done.designs_changed} design{done.designs_changed === 1 ? '' : 's'} updated
          ({Object.entries(done.summary).map(([k, v]) => `${v} ${k}`).join(', ') || 'no link changes'}).
          {done.rejected.length > 0 && ` ${done.rejected.length} row(s) were skipped.`}
        </p>
      )}

      {preview && (
        <div className="mt-3 border-t pt-3">
          <p className="text-sm text-gray-700">
            <b>{preview.rows}</b> rows · <b>{preview.designs_changed}</b> designs will change
            ({s.added || 0} added, {s.replaced || 0} replaced, {s.removed || 0} removed) ·
            {' '}{preview.unchanged} unchanged · <span className={preview.rejected.length ? 'text-zoddle-pink' : ''}>{preview.rejected.length} rejected</span>
          </p>
          {preview.rejected.length > 0 && (
            <div className="mt-2 max-h-40 overflow-y-auto text-xs bg-red-50 rounded-lg p-2">
              {preview.rejected.map(r => (
                <p key={r.line} className="text-gray-700">Line {r.line}{r.design_code ? ` (Design ${r.design_code})` : ''}: <span className="text-zoddle-pink">{r.reason}</span></p>
              ))}
            </div>
          )}
          {preview.changes.length > 0 && (
            <div className="mt-2 max-h-56 overflow-y-auto text-xs bg-gray-50 rounded-lg p-2">
              {preview.changes.map(c => (
                <p key={c.line} className="text-gray-700 truncate">
                  <b>{c.design_code}</b> {c.design_name}: {c.changes.map(x => `${x.action} ${SITE_LABEL[x.site] || x.site}`).join(', ')}
                </p>
              ))}
            </div>
          )}
          <div className="flex gap-2 mt-3">
            <button onClick={doCommit} disabled={busy || preview.designs_changed === 0}
              className="bg-zoddle-green text-white rounded-lg px-4 py-1.5 text-sm font-semibold disabled:opacity-40">
              Save {preview.designs_changed} design{preview.designs_changed === 1 ? '' : 's'}
            </button>
            <button onClick={() => doPreview(null)} disabled={busy} className={btnCls}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function LinksView({ brands, onLinksChanged }) {
  const [brand, setBrand] = useState('');
  const [missing, setMissing] = useState(false);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let live = true;
    setError(null);
    getBenchmarkLinks({ brand, missing, page, page_size: PAGE_SIZE })
      .then(d => { if (live) setData(d); })
      .catch(e => { if (live) setError(errText(e)); });
    return () => { live = false; };
  }, [brand, missing, page, tick]);

  const applied = useCallback(() => { setTick(t => t + 1); onLinksChanged(); }, [onLinksChanged]);
  const pages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const b = brands.find(x => x.brand === brand);

  return (
    <>
      <UploadPanel onApplied={applied} />
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <select value={brand} onChange={e => { setBrand(e.target.value); setPage(1); }} className={selectCls}>
          <option value="">All brands</option>
          {brands.map(x => <option key={x.brand} value={x.brand}>{x.brand} ({x.designs_linked}/{x.designs})</option>)}
        </select>
        <label className="text-xs text-gray-500 flex items-center gap-1">
          <input type="checkbox" checked={missing} onChange={e => { setMissing(e.target.checked); setPage(1); }} />
          Only designs with no links
        </label>
        {b && <span className="text-xs text-gray-400">{b.designs_linked} of {b.designs} designs in stock have links</span>}
        <div className="flex-1" />
        {brand && (
          <button onClick={() => downloadLinksTemplate(brand).catch(e => setError(errText(e)))} className={btnCls}>
            Download {brand}'s current links
          </button>
        )}
      </div>
      {error && <p className="text-xs text-zoddle-pink mb-2">{error}</p>}
      {data && data.designs.length === 0 && <p className="text-sm text-gray-400">No designs match.</p>}
      {data && data.designs.length > 0 && (
        <div className="bg-white border border-gray-100 rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-400 border-b">
                <th className="px-3 py-2">Design</th>
                <th className="px-3 py-2">Links</th>
              </tr>
            </thead>
            <tbody>
              {data.designs.map(d => (
                <tr key={d.design_code} className="border-b last:border-0 align-top">
                  <td className="px-3 py-2">
                    <div className="flex gap-2 items-start">
                      {d.image_url_1
                        ? <img src={cldThumb(d.image_url_1, 96)} alt="" loading="lazy" className="w-10 h-10 object-cover rounded bg-gray-100 flex-shrink-0" />
                        : <div className="w-10 h-10 rounded bg-gray-100 flex-shrink-0" />}
                      <div>
                        <p className="font-mono font-bold text-zoddle-blue text-xs">{d.design_code}</p>
                        <p className="text-xs text-gray-700 max-w-xs truncate" title={d.design_name}>{d.design_name}</p>
                        <p className="text-[11px] text-gray-400">{d.brand} · {d.category}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    {d.links.length === 0 && <span className="text-xs text-gray-300">No links</span>}
                    <div className="flex gap-1.5 flex-wrap">
                      {d.links.map(l => (
                        <a key={l.slot} href={l.url} target="_blank" rel="noreferrer" title={l.problem || l.url}
                          className={`text-[11px] px-2 py-0.5 rounded-full border hover:underline ${
                            l.problem ? 'border-red-200 text-zoddle-pink' : 'border-gray-200 text-gray-600'}`}>
                          {l.slot}. {SITE_LABEL[l.site] || l.site || 'invalid'}
                        </a>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && data.total > PAGE_SIZE && (
        <div className="flex items-center justify-center gap-3 mt-4 text-xs text-gray-500">
          <button onClick={() => setPage(p => p - 1)} disabled={page <= 1} className={btnCls}>← Previous</button>
          <span>Page {page} of {pages} · {data.total.toLocaleString()} designs</span>
          <button onClick={() => setPage(p => p + 1)} disabled={page >= pages} className={btnCls}>Next →</button>
        </div>
      )}
    </>
  );
}

// ------------------------------------------------------------------ health

function siteRate(st) {
  if (!st || !st.links) return null;
  return Math.round(((st.links - (st.not_fetched || 0)) / st.links) * 100);
}

function HealthView() {
  const [runs, setRuns] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => { getBenchmarkRuns(20).then(setRuns).catch(e => setError(errText(e))); }, []);

  const lastDone = (runs || []).find(r => r.status === 'done');
  const weak = lastDone ? Object.entries(lastDone.site_stats || {})
    .filter(([, st]) => st.links >= 3 && siteRate(st) < 50).map(([s]) => SITE_LABEL[s] || s) : [];

  return (
    <>
      <p className="text-xs text-gray-500 mb-3">
        A page counts as <b>read</b> when the site gave an answer — a price, out of stock, or "doesn't sell this size".
        A sudden drop for one site usually means it changed its pages or started refusing us.
      </p>
      {weak.length > 0 && (
        <div className="mb-3 bg-orange-50 border border-orange-100 rounded-lg px-4 py-3 text-sm text-orange-800">
          In the last run, fewer than half the pages were read on: <b>{weak.join(', ')}</b>. Prices there are being kept from earlier reads.
        </div>
      )}
      {error && <p className="text-xs text-zoddle-pink mb-2">{error}</p>}
      {runs && runs.length === 0 && <p className="text-sm text-gray-400">No runs yet.</p>}
      {runs && runs.length > 0 && (
        <div className="bg-white border border-gray-100 rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-400 border-b">
                <th className="px-3 py-2">Started</th><th className="px-3 py-2">Scope</th><th className="px-3 py-2">By</th>
                <th className="px-3 py-2">Status</th><th className="px-3 py-2 text-right">SKUs</th><th className="px-3 py-2 text-right">Pages</th>
                {SITES.map(s => <th key={s.key} className="px-2 py-2 text-right">{s.label}</th>)}
                <th className="px-3 py-2">Audit</th>
              </tr>
            </thead>
            <tbody>
              {runs.map(r => (
                <tr key={r.run_id} className="border-b last:border-0 align-top">
                  <td className="px-3 py-2 whitespace-nowrap">{istTime(r.started_at)}</td>
                  <td className="px-3 py-2">{scopeText(r)}</td>
                  <td className="px-3 py-2 text-gray-500">{r.started_by || '—'}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full ${
                      r.status === 'done' ? 'bg-green-100 text-green-700' : r.status === 'running' ? 'bg-blue-100 text-zoddle-blue'
                        : 'bg-red-100 text-zoddle-pink'}`}>{r.status}</span>
                    {r.error && <p className="text-[11px] text-gray-400 mt-1 max-w-xs">{r.error}</p>}
                  </td>
                  <td className="px-3 py-2 text-right">{r.skus_done}/{r.skus_total}</td>
                  <td className="px-3 py-2 text-right">{r.pages_fetched}</td>
                  {SITES.map(s => {
                    const st = (r.site_stats || {})[s.key];
                    const rate = siteRate(st);
                    return (
                      <td key={s.key} className="px-2 py-2 text-right whitespace-nowrap"
                        title={st ? Object.entries(st).map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`).join('\n') : ''}>
                        {rate === null ? <span className="text-gray-200">—</span> : (
                          <span className={rate < 50 ? 'text-zoddle-pink font-semibold' : rate < 85 ? 'text-orange-600' : 'text-green-700'}>
                            {rate}% <span className="text-[11px] text-gray-400">of {st.links}</span>
                          </span>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-3 py-2">
                    {r.audit ? (r.audit.ok ? <span className="text-xs text-green-700">passed</span>
                      : <span className="text-xs text-zoddle-pink" title={(r.audit.findings || []).map(f => f.detail).join('\n')}>{r.audit.fail} failed</span>)
                      : <span className="text-gray-300">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ------------------------------------------------------------------ page

const VIEWS = [
  { key: 'results', label: 'Results' },
  { key: 'links', label: 'Links' },
  { key: 'health', label: 'Health' },
];

export default function Benchmarking() {
  const [view, setView] = useState('results');
  const [brands, setBrands] = useState([]);
  const loadBrands = useCallback(() => { getBenchmarkBrands().then(setBrands).catch(() => {}); }, []);
  useEffect(() => { loadBrands(); }, [loadBrands]);

  return (
    <div className="p-5 pb-16">
      <div className="flex gap-1 mb-4">
        {VIEWS.map(v => (
          <button key={v.key} onClick={() => setView(v.key)}
            className={`text-sm px-3 py-1.5 rounded-lg ${view === v.key ? 'bg-zoddle-green/10 text-zoddle-green font-semibold' : 'text-gray-500 hover:text-gray-700'}`}>
            {v.label}
          </button>
        ))}
      </div>
      {view === 'results' && <ResultsView brands={brands} />}
      {view === 'links' && <LinksView brands={brands} onLinksChanged={loadBrands} />}
      {view === 'health' && <HealthView />}
    </div>
  );
}
